import { FormProvider } from "react-hook-form";
import useLabInventoryInfo from "./useLabInventoryInfo";
import {
  CustomTableCell,
  CustomTableHead,
  FormCheckbox,
  FormInput,
  FormTextarea,
} from "@/components";
import {
  CURRICULUM_NOTES,
  DESIGNATION,
  EMAIL,
  INCLUDES_3D_PRINTER,
  INCLUDES_CHAIRS,
  INCLUDES_ELECTRICAL,
  INCLUDES_PAINTING,
  INCLUDES_PODIUM_MIC,
  INCLUDES_STORAGE_CABINETS,
  INCLUDES_TABLETS,
  INCLUDES_TEACHERS_TABLE,
  INCLUDES_TV,
  INFRASTRUCTURE_NOTES,
  NAME,
  OTHER_NOTES,
  PHONE,
  POC_DETAILS,
} from "./consts";
import {
  Button,
  Table,
  TableBody,
  TableHeader,
  TableRow,
} from "@/components/ui";
import { Trash2 } from "lucide-react";

const LabInventoryInfo = () => {
  const {
    form,
    requiredFields,
    onSubmit,
    onCancel,
    onAddNewPocDetails,
    onRemovePocDetails,
    scrollRef,
  } = useLabInventoryInfo();
  const { errors } = form.formState;

  return (
    <div className="px-5">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
          <div className="bg-white rounded-lg pt-3 pb-[100px] px-1">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-[16px] text-primary">Lab POC Details</h2>
              <Button
                type="button"
                variant="ghost"
                className="text-primary hover:text-primary"
                onClick={onAddNewPocDetails}
              >
                + Add More
              </Button>
            </div>
            <div className="overflow-x-auto w-full">
              <div className="min-w-[800px] overflow-y-scroll" ref={scrollRef}>
                <Table>
                  <TableHeader>
                    <TableRow className="mb-3 rounded">
                      <CustomTableHead className="w-[50px] text-black font-normal">
                        POC Name
                      </CustomTableHead>
                      <CustomTableHead className="w-[50px] text-black font-normal">
                        POC Phone
                      </CustomTableHead>
                      <CustomTableHead className="w-[50px] text-black font-normal">
                        POC Email
                      </CustomTableHead>
                      <CustomTableHead className="w-[100px] text-black font-normal">
                        POC Designation
                      </CustomTableHead>
                      <CustomTableHead className="w-[50px] text-black font-normal"></CustomTableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.watch(POC_DETAILS)?.map((_: any, index: number) => {
                      return (
                        <TableRow
                          key={`row-${index}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <CustomTableCell>
                            <FormInput
                              name={`${POC_DETAILS}.${index}.${NAME}`}
                              control={form.control}
                              label=""
                              placeholder=""
                              error={
                                (errors[POC_DETAILS] as any)?.[`${index}`]?.[
                                  NAME
                                ]?.message
                              }
                              isRequired={requiredFields.includes(
                                `${POC_DETAILS}[].${NAME}`
                              )}
                            />
                          </CustomTableCell>
                          <CustomTableCell>
                            <FormInput
                              name={`${POC_DETAILS}.${index}.${PHONE}`}
                              control={form.control}
                              label=""
                              placeholder=""
                              error={
                                (errors[POC_DETAILS] as any)?.[`${index}`]?.[
                                  PHONE
                                ]?.message
                              }
                              isRequired={requiredFields.includes(
                                `${POC_DETAILS}[].${PHONE}`
                              )}
                            />
                          </CustomTableCell>
                          <CustomTableCell>
                            <FormInput
                              name={`${POC_DETAILS}.${index}.${EMAIL}`}
                              control={form.control}
                              label=""
                              placeholder=""
                              error={
                                (errors[POC_DETAILS] as any)?.[`${index}`]?.[
                                  EMAIL
                                ]?.message
                              }
                              isRequired={requiredFields.includes(
                                `${POC_DETAILS}[].${EMAIL}`
                              )}
                            />
                          </CustomTableCell>
                          <CustomTableCell>
                            <FormInput
                              name={`${POC_DETAILS}.${index}.${DESIGNATION}`}
                              control={form.control}
                              label=""
                              placeholder=""
                              error={
                                (errors[POC_DETAILS] as any)?.[`${index}`]?.[
                                  DESIGNATION
                                ]?.message
                              }
                              isRequired={requiredFields.includes(
                                `${POC_DETAILS}[].${DESIGNATION}`
                              )}
                            />
                          </CustomTableCell>
                          <CustomTableCell>
                            <Trash2
                              className="stroke-red-600 h-4 cursor-pointer"
                              onClick={() => onRemovePocDetails(index)}
                            />
                          </CustomTableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>

            <>
              <h2 className="text-[16px] text-primary my-5">
                Inventory Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-[25px]">
                <FormCheckbox
                  name={INCLUDES_TV}
                  control={form.control}
                  label="Includes TV?"
                  error={errors[INCLUDES_TV]?.message}
                  isRequired={requiredFields.includes(INCLUDES_TV)}
                />
                <FormCheckbox
                  name={INCLUDES_3D_PRINTER}
                  control={form.control}
                  label="Includes 3D Printer?"
                  error={errors[INCLUDES_3D_PRINTER]?.message}
                  isRequired={requiredFields.includes(INCLUDES_3D_PRINTER)}
                />
                <FormCheckbox
                  name={INCLUDES_PAINTING}
                  control={form.control}
                  label="Includes Painting?"
                  error={errors[INCLUDES_PAINTING]?.message}
                  isRequired={requiredFields.includes(INCLUDES_PAINTING)}
                />
                <FormCheckbox
                  name={INCLUDES_ELECTRICAL}
                  control={form.control}
                  label="Includes Electrical?"
                  error={errors[INCLUDES_ELECTRICAL]?.message}
                  isRequired={requiredFields.includes(INCLUDES_ELECTRICAL)}
                />
                <FormCheckbox
                  name={INCLUDES_STORAGE_CABINETS}
                  control={form.control}
                  label="Includes Storage Cabinets?"
                  error={errors[INCLUDES_STORAGE_CABINETS]?.message}
                  isRequired={requiredFields.includes(
                    INCLUDES_STORAGE_CABINETS
                  )}
                />
                <FormCheckbox
                  name={INCLUDES_TEACHERS_TABLE}
                  control={form.control}
                  label="Includes Teachers Table?"
                  error={errors[INCLUDES_TEACHERS_TABLE]?.message}
                  isRequired={requiredFields.includes(INCLUDES_TEACHERS_TABLE)}
                />
                <FormCheckbox
                  name={INCLUDES_PODIUM_MIC}
                  control={form.control}
                  label="Includes Podium and Mic?"
                  error={errors[INCLUDES_PODIUM_MIC]?.message}
                  isRequired={requiredFields.includes(INCLUDES_PODIUM_MIC)}
                />
                <FormCheckbox
                  name={INCLUDES_TABLETS}
                  control={form.control}
                  label="Includes Tablets?"
                  error={errors[INCLUDES_TABLETS]?.message}
                  isRequired={requiredFields.includes(INCLUDES_TABLETS)}
                />
                <FormCheckbox
                  name={INCLUDES_CHAIRS}
                  control={form.control}
                  label="Includes Chairs?"
                  error={errors[INCLUDES_CHAIRS]?.message}
                  isRequired={requiredFields.includes(INCLUDES_CHAIRS)}
                />
              </div>
              <div className="grid grid-cols-1 gap-[25px] mt-5">
                <FormTextarea
                  name={INFRASTRUCTURE_NOTES}
                  control={form.control}
                  label="Infrastructure Notes"
                  placeholder="Please enter"
                  error={errors[INFRASTRUCTURE_NOTES]?.message}
                  isRequired={requiredFields.includes(INFRASTRUCTURE_NOTES)}
                  maxLength={100}
                />
                <FormTextarea
                  name={CURRICULUM_NOTES}
                  control={form.control}
                  label="Curriculum Notes"
                  placeholder="Please enter"
                  error={errors[CURRICULUM_NOTES]?.message}
                  isRequired={requiredFields.includes(CURRICULUM_NOTES)}
                  maxLength={100}
                />
                <FormTextarea
                  name={OTHER_NOTES}
                  control={form.control}
                  label="Other Notes"
                  placeholder="Please enter"
                  error={errors[OTHER_NOTES]?.message}
                  isRequired={requiredFields.includes(OTHER_NOTES)}
                  maxLength={100}
                />
              </div>
            </>
          </div>
          <div className="flex flex-row justify-end gap-3 py-3 absolute bottom-0 w-full bg-white pb-[25px] z-[3]">
            <Button variant="outline" type="button" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit">{"Save & Next"}</Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default LabInventoryInfo;
